(function () {
  'use strict';

  function countdownState(deadline, now = Date.now()) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(deadline || '')) return { status: 'unset' };
    const end = Date.parse(deadline);
    if (!Number.isFinite(end)) return { status: 'unset' };
    const seconds = Math.max(0, Math.ceil((end - now) / 1000));
    const pad = value => String(value).padStart(2, '0');
    return {
      status: seconds ? 'running' : 'ended',
      days: pad(Math.floor(seconds / 86400)),
      hours: pad(Math.floor(seconds / 3600) % 24),
      minutes: pad(Math.floor(seconds / 60) % 60),
      seconds: pad(seconds % 60)
    };
  }

  async function addCampaignItem(endpoint, variantId, fetcher = fetch) {
    if (!/^\d+$/.test(String(variantId || ''))) throw new Error('Invalid variant');
    const response = await fetcher(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: 1 })
    });
    const product = await response.json();
    if (!response.ok || product.status >= 400) {
      const message = typeof product.description === 'string' ? product.description : '';
      const error = new Error(message || 'Cart request failed');
      error.customerMessage = message;
      throw error;
    }
    return product;
  }

  async function copyCouponCode(code, clipboard = navigator.clipboard) {
    if (!code || !code.trim()) throw new Error('Missing coupon code');
    if (!clipboard?.writeText) throw new Error('Clipboard unavailable');
    await clipboard.writeText(code.trim());
  }

  function selectDailyCouponSet(days, now = Date.now()) {
    if (!Array.isArray(days)) return null;
    return days.find(day => {
      const startsAt = Date.parse(day?.startsAt || '');
      const endsAt = Date.parse(day?.endsAt || '');
      return Number.isFinite(startsAt) && Number.isFinite(endsAt) && now >= startsAt && now <= endsAt && Array.isArray(day.coupons) && day.coupons.length === 8;
    }) || null;
  }

  function buildDailyCouponSet(schedule, now = Date.now()) {
    const startsAt = Date.parse(schedule?.startsAt || '');
    const endsAt = Date.parse(schedule?.endsAt || '');
    if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt) || now < startsAt || now > endsAt) return null;
    const date = new Date(now + (8 * 60 * 60 * 1000)).toISOString().slice(0, 10);
    const startDate = schedule.startsAt.slice(0, 10);
    const daysSinceStart = Math.floor((Date.parse(`${date}T00:00:00+08:00`) - Date.parse(`${startDate}T00:00:00+08:00`)) / 86400000);
    const group = daysSinceStart % 2 === 0 ? 'A' : 'B';
    const coupons = schedule.groups?.[group];
    if (!Array.isArray(coupons) || coupons.length !== 8 && coupons.length !== 1) return null;
    const mmdd = date.slice(5).replace('-', '');
    return { date, group, startsAt: `${date}T00:00:00+08:00`, endsAt: date === '2026-10-28' ? schedule.endsAt : `${date}T23:59:59+08:00`, coupons: coupons.map(coupon => ({ ...coupon, code: `OCT-${coupon.key}-${mmdd}` })) };
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  }

  function storefrontRoot(root) {
    const value = String(root || '/');
    return value.endsWith('/') ? value : `${value}/`;
  }

  function couponCardMarkup(coupon, labels) {
    const title = escapeHtml(coupon.title);
    const code = escapeHtml(coupon.code);
    const href = `${storefrontRoot(labels.root)}products/${encodeURIComponent(coupon.handle)}`;
    return `<article class="ar-coupon"><a class="ar-coupon__top" href="${href}"><div class="ar-coupon__image" data-coupon-image><span aria-hidden="true">✦</span></div><div class="ar-coupon__offer"><h3>${title}</h3><strong>€${escapeHtml(coupon.amount)} OFF</strong></div></a><div class="ar-coupon__tear"><div class="ar-coupon__code-row"><code class="ar-coupon__code">${code}</code><button type="button" class="ar-coupon__copy" data-coupon-copy data-code="${code}" data-success="${escapeHtml(labels.success)}" data-error="${escapeHtml(labels.error)}" aria-label="${escapeHtml(labels.copy)} ${code}">${escapeHtml(labels.copy)}</button></div><p class="ar-coupon__expiry">${escapeHtml(labels.timeRemaining)} <span class="ar-coupon__countdown" data-coupon-countdown data-deadline="${escapeHtml(coupon.endsAt)}" role="timer">--</span></p><p class="ar-coupon__status" role="status" aria-live="polite"></p></div></article>`;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { countdownState, addCampaignItem, copyCouponCode, selectDailyCouponSet, buildDailyCouponSet, couponCardMarkup };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('autumn-ready')) return;

  class AutumnReady extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener('click', async event => {
        const button = event.target.closest('[data-coupon-copy]');
        if (!button || button.disabled) return;
        const status = button.closest('.ar-coupon').querySelector('.ar-coupon__status');
        button.disabled = true;
        status.textContent = '';
        delete status.dataset.state;
        try {
          await copyCouponCode(button.dataset.code);
          status.textContent = button.dataset.success;
        } catch (_) {
          status.dataset.state = 'error';
          status.textContent = button.dataset.error;
        } finally {
          button.disabled = false;
        }
      }, { signal });
      this.loadCoupons();
      this.addEventListener('submit', async event => {
        const form = event.target.closest('form[data-ar-cart]');
        if (!form || !this.contains(form)) return;
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        if (this.cartBusy || button.disabled) return;
        this.cartBusy = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const status = form.closest('.ar-product').querySelector('.ar-cart-status');
        status.textContent = '';
        delete status.dataset.state;
        try {
          const product = await addCampaignItem(form.dataset.endpoint, form.elements.id.value);
          status.textContent = form.dataset.success;
          status.dataset.state = 'success';
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            bubbles: true, detail: { product, addToCartBtn: button }
          }));
          if (window.theme?.settings?.cartType === 'page' || !document.getElementById('CartDrawer')) {
            window.location.assign(window.theme?.routes?.cartPage || `${window.Shopify?.routes?.root || '/'}cart`);
          }
        } catch (error) {
          status.textContent = error.customerMessage || form.dataset.error;
        } finally {
          this.cartBusy = false;
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }, { signal });
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionButton = this.querySelector('[data-motion-toggle]');
      this.paused = this.dataset.motion !== 'true';
      this.updateMotion = () => {
        const paused = this.paused || this.motionPreference.matches;
        this.classList.toggle('ar-motion-paused', paused);
        if (this.motionButton) {
          this.motionButton.setAttribute('aria-pressed', String(paused));
          this.motionButton.textContent = paused ? this.motionButton.dataset.play : this.motionButton.dataset.pause;
        }
      };
      this.motionButton?.addEventListener('click', () => {
        this.paused = !this.classList.contains('ar-motion-paused');
        this.updateMotion();
      }, { signal });
      this.motionPreference.addEventListener('change', this.updateMotion, { signal });
      this.updateMotion();
      this.timer = this.querySelector('[data-countdown]');
      const tick = () => {
        if (!this.timer) return;
        const state = countdownState(this.timer.dataset.deadline);
        this.timer.hidden = state.status === 'unset';
        const ended = this.querySelector('[data-campaign-ended]');
        if (ended) ended.hidden = state.status !== 'ended';
        if (state.status === 'unset') return;
        this.timer.querySelector('[data-countdown-digits]').hidden = state.status === 'ended';
        this.timer.querySelector('[data-countdown-label]').hidden = state.status === 'ended';
        for (const key of ['days', 'hours', 'minutes', 'seconds']) {
          const node = this.timer.querySelector(`[data-unit="${key}"]`);
          if (node && node.textContent !== state[key]) node.textContent = state[key];
        }
      };
      tick();
      if (this.timer && !this.timer.hidden) this.interval = window.setInterval(tick, 1000);
      const nav = this.querySelector('.ar-nav');
      this.sections = [...this.querySelectorAll('[data-campaign-section]')];
      const updateHeader = () => {
        const header = document.getElementById('SiteHeader');
        const wrapper = document.getElementById('HeaderWrapper');
        const sticky = header && (getComputedStyle(header).position === 'fixed' || wrapper?.classList.contains('header-wrapper--sticky'));
        const offset = sticky ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        this.style.setProperty('--ar-header-offset', `${offset}px`);
        const marker = offset + (nav?.offsetHeight || 64) + 64;
        let active = this.sections[0];
        this.sections.forEach(section => { if (section.getBoundingClientRect().top <= marker) active = section; });
        this.querySelectorAll('.ar-nav a').forEach(link => {
          if (active && link.hash === `#${active.id}`) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };
      let pending = false;
      const schedule = () => {
        if (pending) return;
        pending = true;
        this.frame = requestAnimationFrame(() => { pending = false; updateHeader(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      // The theme animates its fixed header after scrolling has stopped.
      const siteHeader = document.getElementById('SiteHeader');
      siteHeader?.addEventListener('transitionend', schedule, { signal });
      siteHeader?.addEventListener('animationend', schedule, { signal });
      updateHeader();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => entry.target.classList.toggle('ar-in-view', entry.isIntersecting));
        }, { rootMargin: '80px' });
        this.querySelectorAll('[data-atmosphere]').forEach(scene => this.observer.observe(scene));
      } else this.querySelectorAll('[data-atmosphere]').forEach(scene => scene.classList.add('ar-in-view'));
      document.addEventListener('visibilitychange', () => {
        this.classList.toggle('ar-tab-hidden', document.hidden);
        if (!document.hidden) tick();
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        event.target.scrollIntoView({ block: 'start', behavior: 'instant' });
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearInterval(this.interval);
      clearInterval(this.couponInterval);
      cancelAnimationFrame(this.frame);
    }

    updateCouponCountdowns() {
      this.querySelectorAll('[data-coupon-countdown]').forEach(node => {
        const state = countdownState(node.dataset.deadline);
        if (state.status === 'unset') return;
        const value = state.status === 'ended' ? '00d : 00h : 00m : 00s' : `${state.days}d : ${state.hours}h : ${state.minutes}m : ${state.seconds}s`;
        if (node.textContent !== value) node.textContent = value;
      });
    }

    async loadCoupons() {
      const zone = this.querySelector('[data-coupon-zone]');
      const grid = this.querySelector('[data-coupon-grid]');
      if (!zone || !grid || !grid.dataset.schedule) return;
      try {
        const response = await fetch(grid.dataset.schedule, { signal: this.abort.signal });
        if (!response.ok) throw new Error('Coupon schedule unavailable');
        const day = buildDailyCouponSet(await response.json());
        if (!day) return;
        const labels = { root: storefrontRoot(grid.dataset.root), copy: grid.dataset.copy, success: grid.dataset.success, error: grid.dataset.error, timeRemaining: grid.dataset.timeRemaining || 'Valid for' };
        grid.innerHTML = day.coupons.map(coupon => couponCardMarkup({ ...coupon, endsAt: day.endsAt }, labels)).join('');
        zone.hidden = false;
        this.updateCouponCountdowns();
        clearInterval(this.couponInterval);
        this.couponInterval = window.setInterval(() => this.updateCouponCountdowns(), 1000);
        await Promise.all(day.coupons.map(async coupon => {
          try {
            const product = await fetch(`${labels.root}products/${encodeURIComponent(coupon.handle)}.js`, { signal: this.abort.signal }).then(result => result.ok ? result.json() : null);
            const image = product?.featured_image || product?.images?.[0];
            const target = grid.querySelector(`[href="${labels.root}products/${CSS.escape(coupon.handle)}"] [data-coupon-image]`);
            if (image && target) target.innerHTML = `<img src="${escapeHtml(image)}" alt="">`;
          } catch (_) {}
        }));
      } catch (_) {
        zone.hidden = true;
      }
    }
  }
  customElements.define('autumn-ready', AutumnReady);
})();
